import { AlertCircle, Bell, Check, Inbox, RefreshCw, UserRoundX, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, type NotificationRecord } from '../api'
import { useAuth } from '../auth-context'
import { formatDateTime } from '../domain'

const POLL_INTERVAL_MS = 45_000

const notificationTypeLabel = (type: string) => ({
  ORDER_CREATED: '新订单',
  ORDER_EXTENDED: '订单加钟',
}[type] ?? type.replaceAll('_', ' '))

export function NotificationCenter() {
  const { authorized, session } = useAuth()
  const staffId = session.user.staffId
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState<NotificationRecord[]>([])
  const [loading, setLoading] = useState(Boolean(staffId))
  const [error, setError] = useState('')
  const [readingId, setReadingId] = useState('')
  const loadingRef = useRef(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLElement>(null)
  const unreadCount = useMemo(() => notifications.filter(item => !item.readAt).length, [notifications])

  const load = useCallback(async (showLoading = false) => {
    if (!staffId || loadingRef.current) return
    loadingRef.current = true
    if (showLoading) setLoading(true)
    setError('')
    try {
      const values = await authorized(token => api.notifications(token, staffId))
      setNotifications(values)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '通知加载失败')
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }, [authorized, staffId])

  useEffect(() => {
    if (!staffId) {
      setNotifications([])
      setLoading(false)
      setError('')
      return
    }
    void load(true)
    const poll = () => { if (document.visibilityState === 'visible') void load(false) }
    const intervalId = window.setInterval(poll, POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', poll)
    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', poll)
    }
  }, [load, staffId])

  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
      if (event.key !== 'Tab') return
      const focusable = [...(panelRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])') ?? [])]
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
      triggerRef.current?.focus()
    }
  }, [open])

  const openPanel = () => {
    setOpen(true)
    if (staffId) void load(notifications.length === 0)
  }

  const markRead = async (id: string) => {
    if (readingId) return
    setReadingId(id)
    setError('')
    try {
      const updated = await authorized(token => api.readNotification(token, id))
      setNotifications(current => current.map(item => item.id === updated.id ? updated : item))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '通知状态更新失败')
    } finally {
      setReadingId('')
    }
  }

  return <>
    <button ref={triggerRef} className="icon-button notification-button" aria-label={unreadCount ? `通知，${unreadCount} 条未读` : '通知'} aria-haspopup="dialog" aria-expanded={open} onClick={openPanel}>
      <Bell size={19} />
      {unreadCount > 0 && <span className="notification-count" aria-hidden="true">{unreadCount > 99 ? '99+' : unreadCount}</span>}
    </button>
    {open && <div className="notification-layer" onMouseDown={event => { if (event.target === event.currentTarget) setOpen(false) }}>
      <section ref={panelRef} className="notification-panel" role="dialog" aria-modal="true" aria-labelledby="notification-title" aria-busy={loading}>
        <div className="notification-head">
          <div><h2 id="notification-title">通知中心</h2><p>{staffId ? `${unreadCount} 条未读` : '当前账号无个人通知身份'}</p></div>
          <div className="notification-head-actions">
            {staffId && <button className="icon-button" aria-label="刷新通知" title="刷新通知" disabled={loading} onClick={() => void load(true)}><RefreshCw className={loading ? 'spin' : ''} size={17} /></button>}
            <button ref={closeRef} className="icon-button" aria-label="关闭通知中心" onClick={() => setOpen(false)}><X size={19} /></button>
          </div>
        </div>
        {error && <div className="notification-error" role="alert"><AlertCircle size={16} /><span>{error}</span><button className="text-button" onClick={() => void load(true)}>重试</button></div>}
        <div className="notification-list" aria-live="polite">
          {!staffId && <div className="notification-empty"><UserRoundX size={28} /><b>未绑定员工</b><span>{session.user.root ? 'Root 账号未绑定员工，暂无可读取的个人通知。' : '当前账号未绑定员工，请联系管理员处理。'}</span></div>}
          {staffId && loading && notifications.length === 0 && <div className="notification-empty"><RefreshCw className="spin" size={25} /><span>正在加载通知...</span></div>}
          {staffId && !loading && !error && notifications.length === 0 && <div className="notification-empty"><Inbox size={28} /><b>暂无通知</b><span>有新的订单动态时会在这里显示。</span></div>}
          {staffId && notifications.length > 0 && <ul>{notifications.map(item => <li className={item.readAt ? 'read' : 'unread'} key={item.id}>
            <span className="notification-dot" aria-hidden="true" />
            <div className="notification-copy">
              <div className="notification-meta"><span>{notificationTypeLabel(item.type)}</span><time dateTime={item.createdAt}>{formatDateTime(item.createdAt)}</time></div>
              <h3>{item.title}</h3>
              <p>{item.content}</p>
              {!item.readAt && <button className="text-button" disabled={Boolean(readingId)} onClick={() => void markRead(item.id)}><Check size={14} />{readingId === item.id ? '处理中...' : '标记已读'}</button>}
            </div>
          </li>)}</ul>}
        </div>
      </section>
    </div>}
  </>
}
